import { ACCENT, NOTICE, SHADOW, SITE_BG, SURFACE, TEXT } from "./palette";

/**
 * `sx` recipes for the surfaces MUI cannot theme for us: page shells, chrome,
 * frosted panels over the mist, and the browse-list bits that sit straight on
 * the page background.
 *
 * Pages import these instead of writing colours inline, so a season change stays
 * inside `palette.js`.
 */

/** Page shell: the backdrop plus a readable default text colour. */
export const pageSurfaceSx = {
    backgroundColor: SITE_BG,
    color: TEXT.primary,
};

/** Header / drawer chrome. */
export const chromeSurfaceSx = {
    backgroundColor: SURFACE.chrome,
    color: TEXT.primary,
    boxShadow: SHADOW.chrome,
};

/** Card surface for hand-rolled cards; MUI `Card`/`Paper` already get this from the theme. */
export const cardSurfaceSx = {
    backgroundColor: SURFACE.card,
    borderColor: SURFACE.border,
    color: TEXT.primary,
    boxShadow: SHADOW.card,
};

/** Inset wells: grid previews, empty slots, anything that used to be white. */
export const sunkenSurfaceSx = {
    backgroundColor: SURFACE.sunken,
    borderColor: SURFACE.border,
};

/** Frosted panel floating over the play mist (toolbars, trays, access gate). */
export const panelSurfaceSx = {
    backgroundColor: SURFACE.panel,
    border: `1px solid ${SURFACE.border}`,
    backdropFilter: "blur(10px)",
    color: TEXT.primary,
};

/** Round icon buttons floating over the play mist (back, hint, settings, timer). */
export const playPillButtonSx = {
    backgroundColor: SURFACE.panel,
    color: TEXT.primary,
    border: `1px solid ${SURFACE.border}`,
    boxShadow: "0 2px 8px rgba(2, 6, 18, 0.45)",
    "&:hover": { backgroundColor: SURFACE.card },
};

/** Tinted callout block: `noticeSurfaceSx("info")` / `noticeSurfaceSx("warning")`. */
export const noticeSurfaceSx = (kind = "info") => {
    const tone = NOTICE[kind] || NOTICE.info;
    return {
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
    };
};

export const textPrimarySx = { color: TEXT.primary };
export const textSecondarySx = { color: TEXT.secondary };

/** Headings that sit straight on the page backdrop (play/select pages). */
export const headingSx = { color: TEXT.primary };
/** Body copy and status lines on the backdrop. */
export const bodySx = { color: TEXT.secondary };

/** Text on the browse lists that sits on the backdrop rather than on a card. */
export const browseTextSx = { color: TEXT.secondary };

/** Browse tag filters: pale outlines, with the selected chip keeping the brand fill. */
export const browseTagChipSx = (selected) =>
    selected
        ? { minHeight: 44, px: 0.5 }
        : {
              minHeight: 44,
              px: 0.5,
              color: TEXT.primary,
              borderColor: SURFACE.borderStrong,
              "&:hover": { bgcolor: SURFACE.hover },
          };

export const browsePagerIconSx = {
    minWidth: 44,
    minHeight: 44,
    color: TEXT.secondary,
    "&.Mui-disabled": { color: TEXT.disabled },
};

/** Filled brand action (Play / Solve / View). */
export const accentButtonSx = {
    backgroundColor: ACCENT.main,
    color: ACCENT.contrastText,
    "&:hover": { backgroundColor: ACCENT.dark },
};

/** Footer links and copyright on the backdrop. */
export const footerLinkSx = {
    color: TEXT.secondary,
    "&:hover": { color: ACCENT.light },
};
